<template lang="pug">
  .product-stars-container(
    :class="{'has-reviews': reviewData.count > 0}"
    v-if="!hideReviews && (reviewData.count > 0 || !condensed)"
  )
    Stars(
      @click="scrollToReviews"
      :class="!condensed && 'clickable'"
      :rating="reviewData.average"
    )
    .review-count(v-if="condensed") ({{ reviewData.count }})
    .expanded(v-else)
      .avg(@click="scrollToReviews") {{reviewData.average}}
      .num(@click="scrollToReviews")
        | {{reviewData.count}} {{__(reviewData.count === 1 ? 'Review' : 'Reviews')}}
      button.link.write-review(@click="writeReview") {{__('Write a review')}}
    .slot
      slot
</template>

<script setup lang="ts">
import type { IProduct } from '@rogue/universal'
import { computed, onBeforeUnmount, onServerPrefetch, ref, watch } from 'vue'
import { useReviewsStore } from '@/js/stores/reviewsStore'
import { useRogue } from '@/js/services/Rogue'
import { closeReviewSubmission, openReviewSubmission } from '@/js/services/reviews/submission'

const props = withDefaults(defineProps<{
  product?: Partial<IProduct>
  condensed?: boolean
}>(), {
  product   : () => ({}),
  condensed : true,
})

const reviewsStore = useReviewsStore()
const rogue = useRogue()

const openedReview = ref(false)

const summaries = computed(() => reviewsStore.summaries)

const reviewData = computed(() => summaries.value[props.product.sku?.toUpperCase() ?? ''] || { count: 0, average: 0 })

const hideReviews = computed(() => !!props.product?.attributes?.rogue_bvcon_hidereviews)

async function load() {
  try {
    await reviewsStore.loadSummaries([props.product.sku ?? ''], rogue)
  } catch {
    rogue.log.warn('Error loading review summaries')
  }
}

watch(() => props.product.sku, () => load())

load()
onServerPrefetch(load)

async function writeReview() {
  rogue.track.track({ type: 'custom', event: 'Write Review Modal', data: { sku: props.product.sku } })
  openedReview.value = true
  await openReviewSubmission(props.product.sku!, rogue)
}

function scrollToReviews() {
  if (props.condensed) {
    return
  }

  rogue.track.track({ type: 'scrollTo', element: 'reviews' })

  const reviews = document.getElementById('customer-reviews')

  if (rogue.isApp() && reviews) {
    reviews.scrollIntoView(true)

    return
  }

  const { body, scrollingElement } = document
  const scroll = rogue.appContext?.platform === 'ios' ? body : scrollingElement

  if (reviews && scroll) {
    scroll.scrollTo({ top: reviews.offsetTop, behavior: 'smooth' })
  }
}

onBeforeUnmount(() => {
  if (openedReview.value) {
    closeReviewSubmission()
  }
})
</script>

<style lang="scss" scoped>
  .product-stars-container {
    font-size: $size-xs;
    height: $size-l;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    text-decoration: none;
    margin-bottom: $space-xxs;
  }

  .clickable {
    cursor: pointer;
  }

  .expanded {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
  }

  .write-review, .avg, .num {
    font-size: $size-xxs;
    color: $color-black;
    cursor: pointer;
  }

  .avg {
    margin-left: $space-xxxs;
  }

  .num {
    margin-left: $space-s;
  }

  .write-review {
    margin-right: $space-xs;
    margin-left: $space-xs;
  }

  .has-reviews .expanded {
    margin-left: $space-xxxs;
  }

  .has-reviews .write-review {
    margin-left: $space-s;
  }

  .review-count {
    flex-shrink: 1;
    margin: 0 0 0 $space-xxxs;
    color: $color-gray-dark;
    font-size: $size-xxs;
  }

  .avg, .num {
    display: none;
  }

  .has-reviews {
    .avg, .num {
      display: block;
    }
  }

  @media (max-width: $mobile) {
    .has-reviews .slot {
      width: 100%;
    }
  }
</style>
